import { setCurrentTemplate, setActiveBulkView, openBulkDashboard } from '@common-reducers/CampaignReducer'
import { handleActiveBulkViewNavigationThunk } from '@common-reducers/CampaignThunks'
import { DBTemplateThunks, TEMPLATE_LIST_LIMIT, templateSelectByQuery } from "@common-reducers/DBServiceThunks"
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import BulkButton from './bulk-button'
import BulkSearchField from './bulk-search-field'
import BulkTemplateDialogRow from './bulk-template-dialog-row'
import LazyLoadingVirtuoso from './lazy-loading-virtuoso'
import { resetScheduledMessageEditor } from '@common/reducers/ScheduledMessageEditorReducer'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common/config/constants'
import { handleAddScheduledMessageCloseThunk } from '@common/thunks/UserMessagesThunks'
import { isExtensionContext } from '@common/services/detect-context'
import { useRouter } from '@common-reducers/hooks/use-router'
import { mainSliceActions } from '@common/reducers/MainReducer'
import { isCampaignManagementPath, newTemplatePath } from '@common/utils/campaign-routes'
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context'

type Props = {
    handleCloseBulkTemplateDialog?: () => void;
}

export default function BulkTemplateDialog({
    handleCloseBulkTemplateDialog,
}: Props) {
    /// selectors/state hooks

    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const router = useRouter();
    const inExtensionContext = isExtensionContext();
    // Non-null under the redesign shell → "New Template" routes to the create
    // sub-route URL. Null for legacy/extension → existing Redux/modal flow.
    const redesignNav = useCampaignsRedesignNav();
    const isRtl = i18n.dir(i18n.language) === 'rtl';
    const editorContext = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.editorContext);
    const isTemplatePickerModal = Boolean(handleCloseBulkTemplateDialog);
    const isCampaignManagementRoute = !inExtensionContext && (
        isCampaignManagementPath(router.pathname) ||
        router.pathname.startsWith('/campaigns')
    );


    const [searchTerm, setSearchTerm] = useState("");




    /// variables


    /// functions
    const handleNewTemplateButtonClick = async () => {
        // Redesign: URL-drive the template CREATE sub-route. The sub-editor seeds
        // a blank template + sets the right bulk view, so skip the Redux/modal flow.
        if (redesignNav) {
            redesignNav.newTemplate();
            return;
        }
        // Reset state before entering editor to ensure clean slate
        await dispatch(resetScheduledMessageEditor());
        
        // Set empty template for new creation
        dispatch(setCurrentTemplate({
            name: t('templates.newTemplateDefaultName'),
            message: '',
            asset: undefined,
            imageId: '',
            sendLinkPreview: false,
            linkPreview: {},
            isPoll: false,
            poll: {},
        }))

        // "New Template" from ScheduledMessageEditor (schedule message context) should open a visible
        // template editor flow, not navigate in the hidden background under the scheduling dialog.
        // When dialog is rendered in campaign management (/launch-campaign), it should never use
        // the schedule-message calendar flow even if editorContext is still at its default value.
        if (editorContext === 'scheduleMessage' && isTemplatePickerModal && !isCampaignManagementRoute) {
            // Calendar and campaigns are full-screen overlays; ensure calendar is closed
            // before opening template editor to avoid stacking/pointer conflicts.
            dispatch(mainSliceActions.toggleCalendarView(false));
            dispatch(setActiveBulkView("campaignNewTemplateViewFromWhatsappView"));
            if (inExtensionContext) {
                dispatch(openBulkDashboard());
            } else {
                // Website: deep-link to /new-template so the editor is reachable via URL.
                router.navigate(newTemplatePath());
            }
            await dispatch(handleAddScheduledMessageCloseThunk());
        } else {
            dispatch(handleActiveBulkViewNavigationThunk({ navigate: "toNewTemplate" }))
            // Website: reflect the editor view in the URL (extension keeps Redux-only).
            if (!inExtensionContext) {
                router.navigate(newTemplatePath());
            }
        }

        if (handleCloseBulkTemplateDialog) {
            handleCloseBulkTemplateDialog()
        }
    }


    return (
        <div dir={isRtl ? 'rtl' : 'ltr'} className="flex flex-col w-full h-full">
            {/* Search and Actions Bar */}
            <div className="flex items-center gap-5 mb-5 mt-4">
                <div className="flex-1 relative">
                    <BulkSearchField
                        isRtl={isRtl}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeHolderString={t('templates.searchPlaceholder')}
                        className="w-full"
                    />
                </div>
                <BulkButton
                    onClick={handleNewTemplateButtonClick}
                    buttonString={t('templates.newTemplate')}
                    Icon={Plus}
                    buttonWidth='auto'
                />
            </div>

            {/* Templates List */}
            <div className="flex-1 min-h-0">
                <LazyLoadingVirtuoso
                    isFullWidth={true}
                    height="100%"
                    rowHeightString='auto'
                    filterObj={{
                        $or: [
                            { 'name': { $regex: searchTerm, $options: 'i' } },
                            { 'message': { $regex: searchTerm, $options: 'i' } }
                        ],
                        deleted: false,
                    }}
                    /* Load the whole template set in ONE page instead of
                       scroll-paginating it.

                       LazyLoadingVirtuoso defaults to the service page size
                       (10) and then pages on `endReached`, but its `loadMore`
                       derives `$skip` from `fliterdList.length` — the
                       CLIENT-filtered list read off the whole store — while the
                       server pages with `$sort: {updatedAt: -1}` and no `_id`
                       tiebreak. On equal `updatedAt` the server's page
                       boundaries move between requests, so rows get skipped and
                       templates silently never appear. Searching makes it worse:
                       the regex narrows the client list, which shrinks `$skip`.

                       Templates are small and bounded (busiest prod account:
                       1,343), so one page sidesteps the paging math entirely.
                       `findObj` is spread into the fetch but NOT into `query`,
                       so it never reaches the sift selector. Its `$sort` would
                       be overridden by the component's own `$sort` — hence the
                       limit only; display order stays `updatedAt` desc, which is
                       what a picker wants. */
                    findObj={{ $limit: TEMPLATE_LIST_LIMIT }}
                    DBThunks={DBTemplateThunks}
                    DBReducer={state => state.DBTemplateReducer}
                    selectByQuery={templateSelectByQuery}
                    Component={BulkTemplateDialogRow}
                />
            </div>
        </div>
    )
}
